/* jc-5-reasons — page behaviors: countdown, carousels, accordions.
   Markup contracts:
     Countdown:  [data-countdown] containing [data-count="h"|"m"|"s"]
     Carousel:   .jc5-carousel[data-autoplay="4000"?]
                   > .jc5-carousel-viewport > .jc5-carousel-track > .jc5-carousel-slide*
                   > .jc5-carousel-arrow[data-dir="prev"|"next"] (anywhere inside root)
     Accordion:  .jc5-accordion-item > .jc5-accordion-toggle + .jc5-accordion-panel      */
(function () {
  "use strict";

  /* ---------- countdown: clock-derived 24h sale timer ----------
     Derived from the DEVICE clock rather than a stored deadline. The original
     kept a private `jc5-sale-deadline` in localStorage set to "first visit +
     24h", so every visitor ran their own window, incognito restarted the sale,
     and the bar froze at 00:00:00 if the page was left open past it.

     The cycle is anchored to 04:00 Europe/Berlin: at 4am Berlin it reads
     24:00:00 and counts down through the day. Anchoring to Berlin rather than
     the viewer's own clock keeps the digits identical for every visitor, and
     puts the reset at 4am German time — the deadest hour of the shop's day.
     DST-safe via Intl (Berlin's own wall clock is what we read).

     Inside the final hour we roll an extra hour on, so the bar never falls
     below 01:00:00 and never reads 00:00:00 — someone checking out at 3am
     should not be looking at an almost-expired offer. The cost is one visible
     jump per day at 03:00 Berlin (01:00:00 snaps up to 01:59:59); that is
     inherent to extending a countdown mid-cycle. */
  function initCountdown(root) {
    var boxes = [].slice.call(root.querySelectorAll("[data-countdown]"))
      .map(function (box) {
        return {
          h: box.querySelector('[data-count="h"]'),
          m: box.querySelector('[data-count="m"]'),
          s: box.querySelector('[data-count="s"]')
        };
      })
      .filter(function (el) { return el.h && el.m && el.s; });
    if (!boxes.length) return;

    var ANCHOR = 4 * 3600;   // 04:00 Berlin — where the 24h cycle restarts
    var DAY = 24 * 3600;
    var HOUR = 3600;

    var berlin = null;
    try {
      berlin = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Europe/Berlin", hourCycle: "h23",
        hour: "2-digit", minute: "2-digit", second: "2-digit"
      });
    } catch (e) { berlin = null; }

    // Seconds elapsed today on Berlin's wall clock. Falls back to the viewer's
    // own clock only if Intl time zones are unavailable.
    function secondsOfDay() {
      if (berlin) {
        var parts = berlin.formatToParts(new Date()), o = {}, i;
        for (i = 0; i < parts.length; i++) o[parts[i].type] = parts[i].value;
        return (+o.hour) * 3600 + (+o.minute) * 60 + (+o.second);
      }
      var d = new Date();
      return d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds();
    }

    function secondsLeft() {
      var left = ANCHOR - secondsOfDay();  // until 4am Berlin
      if (left <= 0) left += DAY;          // already past it → tomorrow's 4am
      if (left < HOUR) left += HOUR;       // never show under an hour / zero
      return left;
    }

    function pad(n) { return String(n).padStart(2, "0"); }

    function tick() {
      var left = secondsLeft();
      var h = pad(Math.floor(left / 3600));
      var m = pad(Math.floor((left % 3600) / 60));
      var s = pad(left % 60);
      for (var i = 0; i < boxes.length; i++) {
        boxes[i].h.textContent = h;
        boxes[i].m.textContent = m;
        boxes[i].s.textContent = s;
      }
    }

    tick();
    // Recomputed from the clock every tick, so it self-corrects after tab
    // throttling or sleep rather than drifting.
    setInterval(tick, 1000);
  }

  /* ---------- carousels ---------- */
  function initCarousels(root) {
    root.querySelectorAll(".jc5-carousel").forEach(function (car) {
      var track = car.querySelector(".jc5-carousel-track");
      var slides = car.querySelectorAll(".jc5-carousel-slide");
      if (!track || slides.length < 2) return;

      var idx = 0, timer = null;
      var autoplay = parseInt(car.getAttribute("data-autoplay") || "0", 10);

      function maxIndex() {
        // stop when the last slide is fully visible (handles multi-per-view)
        var viewport = car.querySelector(".jc5-carousel-viewport");
        var per = Math.max(1, Math.round(viewport.clientWidth / slides[0].offsetWidth));
        return Math.max(0, slides.length - per);
      }
      function goTo(i) {
        var max = maxIndex();
        idx = i < 0 ? max : (i > max ? 0 : i);
        track.style.transform = "translateX(" + (-slides[idx].offsetLeft) + "px)";
      }
      function stop() { if (timer) { clearInterval(timer); timer = null; } }
      function play() {
        stop();
        if (autoplay) timer = setInterval(function () { goTo(idx + 1); }, autoplay);
      }

      car.querySelectorAll(".jc5-carousel-arrow").forEach(function (btn) {
        btn.addEventListener("click", function (e) {
          e.preventDefault();
          goTo(idx + (btn.getAttribute("data-dir") === "next" ? 1 : -1));
          play();
        });
      });

      /* touch / pointer swipe */
      var startX = null;
      car.addEventListener("pointerdown", function (e) { startX = e.clientX; stop(); });
      car.addEventListener("pointerup", function (e) {
        if (startX !== null && Math.abs(e.clientX - startX) > 40) {
          goTo(idx + (e.clientX < startX ? 1 : -1));
        }
        startX = null; play();
      });

      car.addEventListener("mouseenter", stop);
      car.addEventListener("mouseleave", play);
      window.addEventListener("resize", function () { goTo(idx); });
      play();
    });
  }

  /* ---------- accordions ---------- */
  function initAccordions(root) {
    root.querySelectorAll(".jc5-accordion-item").forEach(function (item) {
      var toggle = item.querySelector(".jc5-accordion-toggle");
      var panel = item.querySelector(".jc5-accordion-panel");
      if (!toggle || !panel) return;
      toggle.setAttribute("aria-expanded", "false");
      toggle.addEventListener("click", function () {
        var open = toggle.getAttribute("aria-expanded") === "true";
        toggle.setAttribute("aria-expanded", open ? "false" : "true");
        if (open) {
          panel.style.height = panel.scrollHeight + "px";
          void panel.offsetHeight;
          panel.style.transition = "height .3s ease";
          panel.style.height = "0px";
        } else {
          panel.style.transition = "height .3s ease";
          panel.style.height = panel.scrollHeight + "px";
          panel.addEventListener("transitionend", function done() {
            panel.style.height = "auto";
            panel.style.transition = "";
            panel.removeEventListener("transitionend", done);
          });
        }
      });
    });
  }

  function init() {
    var root = document.querySelector(".jc5-lp") || document.body;
    initCountdown(root);
    initCarousels(root);
    initAccordions(root);
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
